import React, { useCallback, useState } from "react";
import { Alert, Button, Input, LegacyForm, Modal, useMessagedAsyncFn } from "@payping/web-components";
import { ValidEmail, ValidMobileNumber } from "@payping/utils/validation";
import { customerApis } from "@payping/api";

import { optionExists, selectCustomers } from "@/components/DashInvoice/SelectCustomer/selectCustomers";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { OptionType } from "@/core/component/select-create";

const { customerCreateOrFind } = customerApis;

interface FormFields {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  payers?: string[];
}

interface Props {
  visible: boolean;
  onCreateCustomer: (value: OptionType) => void;
  onCancel: () => void;
}

const CreateCustomerModal: React.FC<Props> = ({ onCancel, onCreateCustomer, visible }) => {
  const { t } = useAppTranslation();
  const [phoneHelp, setPhoneHelp] = useState<string>();
  const [emailHelp, setEmailHelp] = useState<string>();
  const [form] = LegacyForm.useForm<FormFields>();

  const checkValidation = useCallback(
    (phone: string, email: string) => {
      let isValid = true;
      if (!email && !phone) {
        setPhoneHelp(t("error.phoneOrEmailRequired"));
        setEmailHelp(t("error.phoneOrEmailRequired"));
        isValid = false;
      } else if (email) {
        if (!ValidEmail(email)) {
          setEmailHelp(t("error.email"));
          isValid = false;
        } else {
          setEmailHelp("");
          setPhoneHelp("");
        }
      } else if (phone) {
        if (!ValidMobileNumber(phone)) {
          setPhoneHelp(t("error.phone"));
          isValid = false;
        } else {
          setEmailHelp("");
          setPhoneHelp("");
        }
      }
      return isValid;
    },
    [t],
  );

  const [{ loading: isCreating }, handleCustomerCreate] = useMessagedAsyncFn(customerCreateOrFind, [], undefined, {
    loading: false,
    success: t("invoice.create.base.createCustomerModal.successMessage") as string,
  });

  const onSubmit = async ({ firstName, lastName, phone, email }: FormFields): Promise<void> => {
    if (checkValidation(phone, email)) {
      const res = await handleCustomerCreate({ email, firstName, lastName, phone });
      form.resetFields();
      if (!optionExists(form.getFieldsValue().payers, selectCustomers(res))) {
        onCreateCustomer(selectCustomers(res));
      }
    }
  };

  const footer = (
    <>
      <Button onClick={onCancel}>{t("invoice.create.base.createCustomerModal.cancel")}</Button>
      <Button type="primary" onClick={form.submit} loading={isCreating}>
        {t("invoice.create.base.createCustomerModal.submit")}
      </Button>
    </>
  );

  return (
    <Modal
      open={visible}
      title={t("invoice.create.base.createCustomerModal.title")}
      footer={footer}
      onCancel={onCancel}
    >
      <LegacyForm<FormFields>
        form={form}
        onValuesChange={(changedValues, values): void => {
          if (Object.keys(changedValues).find((value) => value === "phone" || value === "email")) {
            checkValidation(values.phone, values.email);
          }
        }}
        // @ts-ignore: `onFinish` prop doesn't have proper type
        onFinish={onSubmit}
        layout={"vertical"}
      >
        <LegacyForm.Item<FormFields>
          label={t("invoice.create.base.createCustomerModal.firstName")}
          name="firstName"
          rules={[{ whitespace: true }]}
        >
          <Input />
        </LegacyForm.Item>
        <LegacyForm.Item<FormFields>
          label={t("invoice.create.base.createCustomerModal.lastName")}
          name="lastName"
          rules={[{ whitespace: true }]}
        >
          <Input />
        </LegacyForm.Item>
        <LegacyForm.Item<FormFields>
          label={t("invoice.create.base.createCustomerModal.phoneNumber")}
          name="phone"
          rules={[{ whitespace: true }]}
          validateStatus={phoneHelp ? "error" : "success"}
          help={phoneHelp}
        >
          <Input />
        </LegacyForm.Item>
        <LegacyForm.Item<FormFields>>
          <Alert type={"warning"} className="tw:w-full" title={t("error.onlyPhoneOrEmailRequired")} showIcon />
        </LegacyForm.Item>
        <LegacyForm.Item
          label={t("invoice.create.base.createCustomerModal.email")}
          name="email"
          rules={[{ type: "email" }]}
          validateStatus={emailHelp ? "error" : "success"}
          help={emailHelp}
        >
          <Input />
        </LegacyForm.Item>
      </LegacyForm>
    </Modal>
  );
};

export default CreateCustomerModal;
